import { z } from 'zod';
import { CURRENT_SCHEMA_VERSION, pageElementSchema, createEmptyPage } from '@fotoboek/domain';
import type { PageDocument, PageElement, PhotoFrameElement } from '@fotoboek/domain';

export const templateSchema = z.object({
  schemaVersion: z.literal(CURRENT_SCHEMA_VERSION),
  id: z.string(),
  name: z.string(),
  category: z.string(),
  tags: z.array(z.string()),
  productProfileId: z.string(),
  pageType: z.enum(['inner', 'cover-front', 'cover-back', 'cover-spine', 'spread']),
  pageSizeMm: z.object({ width: z.number(), height: z.number() }),
  thumbnail: z.string().optional(),
  background: z
    .object({ type: z.enum(['color', 'none']), color: z.string() })
    .default({ type: 'color', color: '#ffffff' }),
  elements: z.array(pageElementSchema),
  locks: z
    .object({
      background: z.boolean().default(false),
      elements: z.boolean().default(false),
    })
    .default({}),
  allowedEdits: z
    .object({
      replacePhotos: z.boolean().default(true),
      editText: z.boolean().default(true),
      moveElements: z.boolean().default(true),
    })
    .default({}),
  typography: z
    .object({
      headingFont: z.string().default('Source Serif 4'),
      bodyFont: z.string().default('Source Sans 3'),
      primaryColor: z.string().default('#1a1a1a'),
    })
    .default({}),
  safeZoneBehavior: z.enum(['warn', 'strict', 'ignore']).default('warn'),
});

export type PhotobookTemplate = z.infer<typeof templateSchema>;

const PAGE = 200;
const SAFE = 8;
const GAP = 6;

type ElInput = z.input<typeof pageElementSchema>;

function pf(sid: string, x: number, y: number, w: number, h: number, z = 1): ElInput {
  return {
    id: '00000000-0000-4000-8000-000000000001',
    type: 'photoFrame',
    x,
    y,
    width: w,
    height: h,
    rotation: 0,
    opacity: 1,
    zIndex: z,
    locked: false,
    hidden: false,
    templateSourceId: sid,
    assetId: null,
    placement: { scale: 1, offsetX: 0, offsetY: 0, rotation: 0, flipX: false, flipY: false },
    filters: { brightness: 0, contrast: 0, saturation: 0, warmth: 0, grayscale: false },
    cornerRadiusMm: 0,
    required: false,
  };
}

function tx(
  sid: string,
  x: number,
  y: number,
  w: number,
  h: number,
  content: string,
  size = 18,
  z = 2,
): ElInput {
  return {
    id: '00000000-0000-4000-8000-000000000002',
    type: 'text',
    x,
    y,
    width: w,
    height: h,
    rotation: 0,
    opacity: 1,
    zIndex: z,
    locked: false,
    hidden: false,
    templateSourceId: sid,
    content,
    fontFamily: 'Source Serif 4',
    fontSizePt: size,
    fontWeight: 400,
    fontStyle: 'normal',
    lineHeight: 1.3,
    letterSpacingEm: 0,
    align: 'left',
    color: '#1a1a1a',
    backgroundColor: null,
    autoHeight: true,
    verticalAlign: 'top',
  };
}

function sh(sid: string, x: number, y: number, w: number, h: number, fill: string, z = 0): ElInput {
  return {
    id: '00000000-0000-4000-8000-000000000003',
    type: 'shape',
    shape: 'rectangle',
    x,
    y,
    width: w,
    height: h,
    rotation: 0,
    opacity: 1,
    zIndex: z,
    locked: false,
    hidden: false,
    templateSourceId: sid,
    fill,
    stroke: null,
    strokeWidthMm: 0,
    cornerRadiusMm: 0,
  };
}

const half = (PAGE - SAFE * 2 - GAP) / 2;
const third = (PAGE - SAFE * 2 - GAP * 2) / 3;

const DEFINITIONS: Array<
  Omit<z.input<typeof templateSchema>, 'schemaVersion' | 'productProfileId' | 'pageSizeMm'>
> = [
  {
    id: 'page-full-bleed',
    name: 'Volledig beeld',
    category: 'foto',
    tags: ['vol'],
    pageType: 'inner',
    background: { type: 'color', color: '#ffffff' },
    elements: [pf('ph1', 0, 0, PAGE, PAGE)],
  },
  {
    id: 'page-single-white',
    name: 'Één foto met witruimte',
    category: 'foto',
    tags: ['witruimte'],
    pageType: 'inner',
    background: { type: 'color', color: '#ffffff' },
    elements: [pf('ph1', 30, 30, 140, 140)],
  },
  {
    id: 'page-two-horizontal',
    name: "Twee foto's 50/50 horizontaal",
    category: 'grid',
    tags: ['twee'],
    pageType: 'inner',
    background: { type: 'color', color: '#ffffff' },
    elements: [
      pf('ph1', SAFE, SAFE, half, PAGE - SAFE * 2),
      pf('ph2', SAFE + half + GAP, SAFE, half, PAGE - SAFE * 2),
    ],
  },
  {
    id: 'page-two-vertical',
    name: "Twee foto's verticaal",
    category: 'grid',
    tags: ['twee'],
    pageType: 'inner',
    background: { type: 'color', color: '#ffffff' },
    elements: [
      pf('ph1', SAFE, SAFE, PAGE - SAFE * 2, half),
      pf('ph2', SAFE, SAFE + half + GAP, PAGE - SAFE * 2, half),
    ],
  },
  {
    id: 'page-hero-two-small',
    name: 'Grote foto plus twee kleine',
    category: 'grid',
    tags: ['drie'],
    pageType: 'inner',
    background: { type: 'color', color: '#ffffff' },
    elements: [
      pf('ph1', SAFE, SAFE, PAGE - SAFE * 2, 118),
      pf('ph2', SAFE, SAFE + 118 + GAP, half, 60),
      pf('ph3', SAFE + half + GAP, SAFE + 118 + GAP, half, 60),
    ],
  },
  {
    id: 'page-three-equal',
    name: "Drie gelijke foto's",
    category: 'grid',
    tags: ['drie'],
    pageType: 'inner',
    background: { type: 'color', color: '#ffffff' },
    elements: [0, 1, 2].map((i) => pf(`ph${i + 1}`, SAFE + i * (third + GAP), 40, third, 120)),
  },
  {
    id: 'page-four-grid',
    name: 'Vierfoto-grid',
    category: 'grid',
    tags: ['vier'],
    pageType: 'inner',
    background: { type: 'color', color: '#ffffff' },
    elements: [
      pf('ph1', SAFE, SAFE, half, half),
      pf('ph2', SAFE + half + GAP, SAFE, half, half),
      pf('ph3', SAFE, SAFE + half + GAP, half, half),
      pf('ph4', SAFE + half + GAP, SAFE + half + GAP, half, half),
    ],
  },
  {
    id: 'page-mosaic',
    name: 'Mozaïek',
    category: 'grid',
    tags: ['mozaïek'],
    pageType: 'inner',
    background: { type: 'color', color: '#ffffff' },
    elements: [
      pf('ph1', SAFE, SAFE, 110, 110),
      pf('ph2', SAFE + 110 + GAP, SAFE, 68, 52),
      pf('ph3', SAFE + 110 + GAP, SAFE + 52 + GAP, 68, 52),
      pf('ph4', SAFE, SAFE + 110 + GAP, 54, 66),
      pf('ph5', SAFE + 54 + GAP, SAFE + 110 + GAP, 124, 66),
    ],
  },
  {
    id: 'page-photo-caption',
    name: 'Foto met titel en onderschrift',
    category: 'tekst',
    tags: ['titel'],
    pageType: 'inner',
    background: { type: 'color', color: '#ffffff' },
    elements: [
      pf('ph1', SAFE, SAFE, PAGE - SAFE * 2, 130),
      tx('t1', SAFE, 145, PAGE - SAFE * 2, 20, 'Titel van dit moment', 22),
      tx('t2', SAFE, 168, PAGE - SAFE * 2, 24, 'Een korte beschrijving van de foto.', 12),
    ],
  },
  {
    id: 'page-timeline',
    name: 'Tijdlijnachtige indeling',
    category: 'tekst',
    tags: ['tijdlijn'],
    pageType: 'inner',
    background: { type: 'color', color: '#ffffff' },
    elements: [
      sh('s1', 28, SAFE, 1.5, PAGE - SAFE * 2, '#8a8174'),
      pf('ph1', 48, 20, 130, 50),
      tx('t1', 48, 74, 130, 14, 'Ochtend', 11),
      pf('ph2', 48, 95, 130, 50),
      tx('t2', 48, 149, 130, 14, 'Middag', 11),
      pf('ph3', 48, 170, 130, 22),
    ],
  },
  {
    id: 'page-quiet-text',
    name: 'Rustige tekstpagina',
    category: 'tekst',
    tags: ['tekst'],
    pageType: 'inner',
    background: { type: 'color', color: '#ffffff' },
    elements: [
      tx('t1', 28, 60, 144, 30, 'Een stil moment', 28),
      tx(
        't2',
        28,
        100,
        144,
        60,
        'Hier past een citaat, een brief of een korte herinnering die het boek tot leven brengt.',
        14,
      ),
    ],
  },
  {
    id: 'page-empty',
    name: 'Lege pagina',
    category: 'leeg',
    tags: ['leeg'],
    pageType: 'inner',
    background: { type: 'color', color: '#ffffff' },
    elements: [],
  },
  {
    id: 'cover-front-hero',
    name: 'Voorkaft — heldere foto',
    category: 'cover',
    tags: ['voorkaft'],
    pageType: 'cover-front',
    background: { type: 'color', color: '#f7f4ef' },
    elements: [pf('ph1', 0, 0, PAGE, PAGE), tx('t1', 20, 150, 160, 30, 'Ons fotoboek', 28)],
  },
  {
    id: 'cover-front-classic',
    name: 'Voorkaft — klassiek kader',
    category: 'cover',
    tags: ['voorkaft'],
    pageType: 'cover-front',
    background: { type: 'color', color: '#f7f4ef' },
    elements: [
      sh('s1', 16, 16, 168, 168, '#f0ebe3'),
      pf('ph1', 28, 28, 144, 120),
      tx('t1', 28, 158, 144, 20, 'Herinneringen', 20),
    ],
  },
  {
    id: 'cover-back-simple',
    name: 'Achterkaft — rustig',
    category: 'cover',
    tags: ['achterkaft'],
    pageType: 'cover-back',
    background: { type: 'color', color: '#f7f4ef' },
    elements: [tx('t1', 24, 160, 152, 24, 'Gemaakt met Fotoboek Studio', 11)],
  },
];

function parseDefinition(def: (typeof DEFINITIONS)[number]): PhotobookTemplate {
  return templateSchema.parse({
    schemaVersion: CURRENT_SCHEMA_VERSION,
    productProfileId: 'square-200-matte',
    pageSizeMm: { width: 200, height: 200 },
    locks: {},
    allowedEdits: {},
    typography: {},
    safeZoneBehavior: 'warn',
    ...def,
  });
}

export const BUILTIN_TEMPLATES: PhotobookTemplate[] = DEFINITIONS.map(parseDefinition);

export function instantiateTemplate(templateId: string): PhotobookTemplate {
  const found = DEFINITIONS.find((t) => t.id === templateId);
  if (!found) throw new Error(`Template niet gevonden: ${templateId}`);
  const base = parseDefinition(found);
  return {
    ...base,
    elements: base.elements.map((el) => ({
      ...el,
      id: crypto.randomUUID(),
    })),
  };
}

export function listTemplates(filter?: { pageType?: PhotobookTemplate['pageType'] }) {
  return BUILTIN_TEMPLATES.filter((t) => !filter?.pageType || t.pageType === filter.pageType);
}

export function getTemplate(id: string): PhotobookTemplate {
  const t = BUILTIN_TEMPLATES.find((x) => x.id === id);
  if (!t) throw new Error(`Template niet gevonden: ${id}`);
  return t;
}

export type ApplyTemplateOptions = {
  redistributePhotos?: boolean;
};

export function applyTemplateToPage(
  page: PageDocument,
  templateId: string,
  options: ApplyTemplateOptions = {},
): PageDocument {
  const template = instantiateTemplate(templateId);
  const previousPhotos = page.elements.filter(
    (e): e is PhotoFrameElement => e.type === 'photoFrame' && !!e.assetId,
  );
  const assetQueue = previousPhotos.map((p) => p.assetId!);

  const elements: PageElement[] = template.elements.map((el) => {
    if (el.type === 'photoFrame' && options.redistributePhotos && assetQueue.length > 0) {
      return { ...el, assetId: assetQueue.shift()! };
    }
    return el;
  });

  return {
    ...page,
    background: template.background,
    elements,
    templateId: template.id,
  };
}

export { createEmptyPage };
